import { onUnmounted, ref } from 'vue';
import { AxiosError, isAxiosError } from 'axios';
import { useMutation, type MutationKey } from '@tanstack/vue-query';
import { EvMessage } from 'evui';
import { type PromiseAxiosResponse } from '@/worker/commands/config/apiInstance';
import { convertRequestTime } from '@/common/utils/commonUtils';
import { i18n, useInternational, type MessageSchemaStringType } from '@/common/locale';
import { FieldMissingError } from '@/database/adapter/adapters';
import { useMessage } from '../inject/useMessage';

const useAbortApi = () => {
  let controller = new AbortController();
  const getSignal = () => controller.signal;
  const abortApi = () => {
    controller.abort();
    controller = new AbortController();
  };

  return {
    abortApi,
    getSignal,
  };
};

/**
 * `useAbortApi` + `useMutation` 조합 헬퍼.
 *
 * 요청마다 최신 `AbortSignal` 을 주입하고, 반환된 `abort()` 로 진행 중이던 요청을 취소할 수 있게 한다.
 * 실시간 뷰의 "필터 변경 등 사용자 액션 시에만 이전 요청 취소, 폴링은 유지" 정책을,
 * 뷰마다 `useAbortApi`/`useMutation` 를 직접 배선하는 중복 없이 적용하기 위한 것이다.
 * (k8s `createAbortableMutation` 과 동일한 목적)
 *
 * @param mutationKey vue-query mutationKey
 * @param mutationFn `(variables, signal) => Promise` — `signal` 을 생성된 API 함수의 마지막 인자로 넘긴다
 * @returns `mutation`(vue-query 반환 객체) + `abort`(진행 중 요청 취소 후 컨트롤러 재생성)
 */
const useAbortableMutation = <TData, TVariables>(
  mutationKey: MutationKey,
  mutationFn: (variables: TVariables, signal: AbortSignal) => Promise<TData>,
) => {
  const { abortApi, getSignal } = useAbortApi();
  const mutation = useMutation({
    mutationKey,
    mutationFn: (variables: TVariables) => mutationFn(variables, getSignal()),
  });

  return { mutation, abort: abortApi };
};

// in main thread
const useRtmApi = () => {
  const { abortApi, getSignal } = useAbortApi();
  const { t } = useInternational();
  const requestTime = ref<string>('');

  const { showErrorMsg, showSuccessMsg } = useMessage();

  /* eslint-disable @typescript-eslint/no-explicit-any */
  const callApi = async <Fn extends (p: any) => PromiseAxiosResponse<any>>({
    fn,
    params,
    isTimeout = true,
    successMsg,
    errorMsg,
    isShowErrorMsg = true,
    frameName,
    validateStatus,
    isPolling = false,
  }: {
    fn: Fn;
    params?: Omit<Parameters<Fn>[0], 'frameName' | 'signal' | 'isTimeout'>;
    isTimeout?: boolean;
    successMsg?: MessageSchemaStringType;
    errorMsg?:
      | MessageSchemaStringType
      | ((error: { status: number }) => MessageSchemaStringType | null | undefined);
    isShowErrorMsg?: boolean;
    frameName?: string;
    validateStatus?: ((status: number) => boolean) | null;
    isPolling?: boolean;
  }) => {
    let state: Awaited<ReturnType<Fn>>['data']['data'];
    let page: any;
    let error:
      | AxiosError<{
          errorType: string;
          message: string;
        }>
      | null
      | { ignoreRequest: boolean } = null;
    if (!isPolling && isTimeout) abortApi();
    try {
      const { data } = await fn({
        ...params,
        ...(isPolling ? { isPolling } : { isTimeout }),
        frameName,
        signal: getSignal(),
      });
      state = data.data;
      page = data.page;
      requestTime.value = convertRequestTime();

      if (successMsg) {
        showSuccessMsg({ msgStr: t(successMsg) });
      }
    } catch (e: unknown) {
      const typedError = e as AxiosError<{ errorType: string; message: string }>;
      error = typedError;
      state = [];
      if (isShowErrorMsg && typedError?.response?.status) {
        const { status } = typedError.response;
        const errorMsgSchema = typeof errorMsg === 'function' ? errorMsg({ status }) : errorMsg;

        if (validateStatus && !validateStatus(typedError.response.status)) {
          showErrorMsg({ msgStr: t(errorMsgSchema ?? 'NOTI.PRODUCT.TEMP_SERVER_ERROR') });
        } else if (status === 406) {
          /*
           * Axios Interceptor에서 406 오류에 대한 처리를 해주기 때문에
           * apiUtils.ts 파일에서는 406 응답을 받더라도 아무런 동작을 하지 않습니다.
           */
        } else if (status === 409) {
          showErrorMsg({ msgStr: t('NOTI.VALIDATION.DUPLICATE') });
        } else if (status === 404) {
          showErrorMsg({ msgStr: t('MESSAGE.NOT_FOUND_TARGET') });
        } else {
          showErrorMsg({ msgStr: t(errorMsgSchema ?? 'NOTI.PRODUCT.TEMP_SERVER_ERROR') });
        }
      }
    }

    return {
      data: state,
      page,
      requestTime: requestTime.value,
      error,
    };
  };

  onUnmounted(() => {
    abortApi();
  });

  return {
    callApi,
    abortApi,
  };
};

const showEvMessage = (type: string, message: string) => {
  EvMessage({
    type,
    message,
  });
};
const statusController = {
  406: (callback?: () => any) => {
    if (typeof callback === 'function') {
      callback();
    }
  },
};
const exceptionHandlerByErrorStatus = ({
  error,
  callback,
}: {
  error?: unknown;
  callback?: () => any;
}) => {
  const typedError = error as AxiosError<{ errorType?: string; message?: string; type?: string }>;
  const { status } = typedError?.response ?? {};

  if (status) {
    statusController?.[status]?.(callback);
  }
};
const paExceptionHandler = ({
  error,
  isShowRejectMsg = false,
  isTempServerError = false,
}: {
  error?: unknown;
  isShowRejectMsg?: boolean;
  isTempServerError?: boolean;
}) => {
  const typedError = error as AxiosError<{ errorType?: string; message?: string; type?: string }>;
  const { status, data } = typedError?.response ?? {};

  if (status === 406) {
    return;
  }
  if (isShowRejectMsg) {
    if (error instanceof FieldMissingError) {
      const msg = i18n.global.t('MESSAGE.LINK_MISSING_REQUIRED_PARAMS', {
        type: error.type ? `${error.type} ` : '',
        value: error.value,
      });
      showEvMessage('error', msg);
      return;
    }

    const errorType = data?.errorType || data?.type || 'Exception';
    const isNotFoundTarget = status === 404 && errorType === 'Exception';
    const msg = i18n.global.t(
      isNotFoundTarget ? 'MESSAGE.NOT_FOUND_TARGET' : `ERROR.API.${errorType}`,
    );
    if (isTempServerError || msg.startsWith('ERROR.API.')) {
      showEvMessage('error', i18n.global.t('NOTI.PRODUCT.TEMP_SERVER_ERROR'));
    } else {
      showEvMessage('error', msg);
    }
  }
};

/**
 * callApi의 에러 처리 로직을 그대로 적용한 핸들러
 * - 406: 무시 (Axios Interceptor에서 처리)
 * - 409: 중복 메시지
 * - 404: 타겟 없음 메시지
 * - 기타: 서버 에러 메시지
 */
const handleApiError = (error: unknown) => {
  if (!isAxiosError(error)) {
    return;
  }

  const status = error.response?.status;

  if (!status) {
    return;
  }

  if (status === 406) {
    // Axios Interceptor에서 처리
    return;
  }

  if (status === 409) {
    showEvMessage('error', i18n.global.t('NOTI.VALIDATION.DUPLICATE'));
  } else if (status === 404) {
    showEvMessage('error', i18n.global.t('MESSAGE.NOT_FOUND_TARGET'));
  } else {
    showEvMessage('error', i18n.global.t('NOTI.PRODUCT.TEMP_SERVER_ERROR'));
  }
};

export {
  useAbortApi,
  useAbortableMutation,
  useRtmApi,
  exceptionHandlerByErrorStatus,
  paExceptionHandler,
  handleApiError,
};
